import React, { CSSProperties } from "react";

import PropTypes from "prop-types";
import styled from "styled-components";

import {
    BIG_NAV_PX_HEIGHT,
    SMALL_NAV_PX_HEIGHT,
    colorCSS,
    fontSizes,
    minWidth,
    shadow,
    theme,
    zIndex
} from "../../../styles";

const BackButtonStyled = styled.button<{ hidden?: string }>({
    alignItems: "center",
    background: "transparent",
    border: 0,
    color: colorCSS("N800"),
    display: "flex",
    gap: "0.5rem",
    opacity: (props) => (props.hidden === "true" ? 0 : 1),
    padding: "1rem",
    transition: "opacity 0.3s ease",
    transitionDelay: "0.2s"
});

export const BackButton: React.FC<{
    "children": React.ReactNode;
    "data-hidden"?: string;
    "onClick"?: () => void;
}> = ({ children, "data-hidden": hidden, onClick }) => (
    <BackButtonStyled hidden={hidden} data-hidden={hidden} onClick={onClick}>
        {children}
    </BackButtonStyled>
);

const ButtonStyled = styled.a({
    "backgroundColor": colorCSS("N0"),
    "background-color": colorCSS("N0"),
    "border": `1px solid ${colorCSS("B300")}`,
    "color": colorCSS("N800"),
    "display": "block",
    "padding": "1rem",
    "textAlign": "center",
    "text-align": "center",
    "textDecoration": "none",
    "text-decoration": "none"
});

export const Button: React.FC<{ children: React.ReactNode; href: string }> = ({
    children,
    href
}) => <ButtonStyled href={href}>{children}</ButtonStyled>;

const ButtonDarkStyled = styled.a<{
    "isActive": boolean;
    "is-active": boolean;
}>({
    backgroundColor: (props) =>
        props.isActive ? colorCSS("N100") : colorCSS("N800"),
    border: `1px solid ${(props) =>
        props.isActive ? colorCSS("N100") : colorCSS("N800")}`,
    color: colorCSS("N0"),
    display: "block",
    padding: "1rem",
    textAlign: "center",
    textDecoration: "none"
});

export const ButtonDark: React.FC<{
    children: React.ReactNode;
    href: string;
}> = ({ children, href }) => {
    const [isActive, setIsActive] = React.useState(false);

    return (
        <ButtonDarkStyled
            isActive={isActive}
            href={href}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            // eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
            onMouseLeave={() => setIsActive(false)}
        >
            {children}
        </ButtonDarkStyled>
    );
};

const ButtonPrimaryStyled = styled.a<{
    "isActive": boolean;
    "is-active": boolean;
}>({
    backgroundColor: (props) =>
        props.isActive ? colorCSS("B500") : colorCSS("B300"),
    border: `1px solid ${(props) =>
        props.isActive ? colorCSS("B500") : colorCSS("B300")}`,
    color: colorCSS("N0"),
    display: "block",
    padding: "1rem",
    textAlign: "center",
    textDecoration: "none"
});

export const ButtonPrimary: React.FC<{
    children: React.ReactNode;
    href: string;
}> = ({ children, href }) => {
    const [isActive, setIsActive] = React.useState(false);

    return (
        <ButtonPrimaryStyled
            isActive={isActive}
            href={href}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            // eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
            onMouseLeave={() => setIsActive(false)}
        >
            {children}
        </ButtonPrimaryStyled>
    );
};

const ButtonsStyled = styled.div({
    "display": "flex",
    "flexDirection": "column",
    "flex-direction": "column",
    "gap": "1rem"
});

export const Buttons: React.FC<{ children: React.ReactNode }> = ({
    children
}) => <ButtonsStyled>{children}</ButtonsStyled>;

const HeaderStyled = styled.header.withConfig({
    shouldForwardProp: (prop) =>
        !["isScrolled", "isOpen", "startTransparent"].includes(prop)
})<{
    startTransparent?: boolean;
    isScrolled: boolean;
    isOpen: boolean;
}>((props) => ({
    "backgroundColor":
        props.startTransparent && !props.isScrolled && !props.isOpen
            ? colorCSS("N0", 0)
            : colorCSS("N0", 1),
    "height": props.isOpen ? "100%" : `${SMALL_NAV_PX_HEIGHT}px`,
    "overflow": "hidden",
    "position": "fixed",
    "top": 0,
    "boxShadow": props.isScrolled || props.isOpen ? shadow.normal : "none",
    "transition": props.isOpen
        ? "height 0.3s linear 0.1s, box-shadow 0.1s linear, background-color 0.1s linear"
        : "height 0.3s linear, box-shadow 0.3s linear 0.3s, background-color 0.3s linear 0.3s",
    "width": "100%",
    "zIndex": zIndex.headerShroud,
    "@media (min-width: 992px)": {
        display: "none"
    }
}));

export const Header: React.FC<{
    "children": React.ReactNode;
    "startTransparent"?: boolean;
    "data-scrolled"?: string;
    "data-open"?: string;
}> = ({
    children,
    startTransparent,
    "data-scrolled": scrolled,
    "data-open": open
}) => {
    const isScrolled = scrolled === "true";
    const isOpen = open === "true";

    return (
        <HeaderStyled
            startTransparent={startTransparent}
            isScrolled={isScrolled}
            isOpen={isOpen}
            data-scrolled={scrolled}
            data-open={open}
        >
            {children}
        </HeaderStyled>
    );
};

const LinkStyled = styled.a({
    "color": colorCSS("N800"),
    "textDecoration": "none",
    "text-decoration": "none"
});

export const Link: React.FC<{ children: React.ReactNode; href: string }> = ({
    children,
    href
}) => <LinkStyled href={href}>{children}</LinkStyled>;

const LogoWrapperStyled = styled.div<{ hidden?: string }>({
    opacity: (props) => (props.hidden === "true" ? 0 : 1),
    transition: "all 0.3s ease",
    transitionDelay: "0.2s",
    marginLeft: (props) => (props.hidden === "true" ? "-141.69px" : "0")
});

export const LogoWrapper: React.FC<{
    "children": React.ReactNode;
    "data-hidden"?: string;
}> = ({ children, "data-hidden": hidden }) => (
    <LogoWrapperStyled hidden={hidden} data-hidden={hidden}>
        {children}
    </LogoWrapperStyled>
);

const MenuButtonStyled = styled.button({
    "alignItems": "center",
    "align-items": "center",
    "backgroundColor": "transparent",
    "background-color": "transparent",
    "display": "flex",
    "border": 0,
    "height": "3.5rem",
    "justifyContent": "center",
    "justify-content": "center",
    "marginLeft": "auto",
    "margin-left": "auto",
    "overflow": "hidden",
    "padding": 0,
    "width": "3.5rem"
});

export const MenuButton: React.FC<{
    children: React.ReactNode;
    onClick?: () => void;
}> = ({ children, onClick }) => (
    <MenuButtonStyled onClick={onClick}>{children}</MenuButtonStyled>
);

const navBarStyle: CSSProperties = {
    alignItems: "center",
    display: "flex",
    height: `${SMALL_NAV_PX_HEIGHT}px`,
    justifyContent: "space-between",
    padding: "0 1rem",
    zIndex: zIndex.header
};

type NavBarProps = {
    children: React.ReactNode;
};

export const NavBar = React.forwardRef<HTMLDivElement, NavBarProps>(
    ({ children }, ref) => (
        <div ref={ref} style={navBarStyle}>
            <style>{`
            @media ${minWidth.lg} {
                .navbar { height: ${BIG_NAV_PX_HEIGHT}px; }
            }
        `}</style>
            <div className="navbar" style={{ display: "contents" }}>
                {children}
            </div>
        </div>
    )
);

NavBar.displayName = "NavBar";
NavBar.propTypes = {
    children: PropTypes.node.isRequired
};

export const NavLinkContainer: React.FC<{ children: React.ReactNode }> = ({
    children
}) => (
    <div style={{ display: "block" }}>
        <style>{`
            .nav-link-container > * {
                padding-left: 0 !important;
            }
        `}</style>
        <div className="nav-link-container">{children}</div>
    </div>
);

export const Page: React.FC<{
    "children": React.ReactNode;
    "data-offscreen"?: string;
}> = ({ children, "data-offscreen": offscreen }) => (
    <div
        style={{
            backgroundColor: colorCSS("N0"),
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            left:
                offscreen === "left"
                    ? "-100vw"
                    : offscreen === "right"
                    ? "100vw"
                    : 0,
            margin: "0 auto",
            maxWidth: "100%",
            overflow: "auto",
            padding: "1rem",
            position: "absolute",
            top: `${SMALL_NAV_PX_HEIGHT}px`,
            transition: "left 0.4s ease",
            width: "100%"
        }}
        data-offscreen={offscreen}
    >
        <style>{`
            .page-container h2 {
                margin-bottom: 0;
                color: ${colorCSS("N800")};
                font-family: ${theme.fontFamily.text};
                font-size: ${fontSizes.sm};
                font-weight: 500;
            }
            .page-container h3 {
                color: ${colorCSS("N800")};
                font-family: ${theme.fontFamily.text};
                font-size: ${fontSizes.sm};
                font-weight: 500;
            }
            .page-container p:last-child {
                margin-bottom: 0;
            }
        `}</style>
        <div className="page-container">{children}</div>
    </div>
);

export const PageContainer: React.FC<{
    children: React.ReactNode;
    open: boolean;
}> = ({ children, open }) => (
    <div
        style={{
            opacity: open ? 1 : 0,
            transition: "opacity 0.6s ease",
            transitionDelay: "0.1s"
        }}
    >
        {children}
    </div>
);

export const Secondary: React.FC<{ children: React.ReactNode }> = ({
    children
}) => (
    <div
        style={{
            padding: "1.5rem 1rem",
            margin: "0 auto",
            maxWidth: "100%",
            backgroundColor: colorCSS("P50", 0.4)
        }}
    >
        <style>{`
            .secondary p {
                color: ${colorCSS("N400")};
                font-size: ${fontSizes.xxs};
            }
            .secondary a {
                margin-bottom: 1.5rem;
            }
            .secondary a:last-of-type {
                margin-bottom: 0;
            }
        `}</style>
        <div className="secondary">{children}</div>
    </div>
);

export const SecondaryCtaContainer: React.FC<{ children: React.ReactNode }> = ({
    children
}) => (
    <div>
        <style>{`
            .secondary-cta-container a {
                margin-top: 1rem;
            }
        `}</style>
        <div className="secondary-cta-container">{children}</div>
    </div>
);

const TabStyled = styled.button({
    "background": "transparent",
    "border": 0,
    "color": colorCSS("N800"),
    "display": "flex",
    "justifyContent": "space-between",
    "justify-content": "space-between",
    "padding": "0 0.5rem 0 0",
    "width": "100%"
});

export const Tab: React.FC<{
    children: React.ReactNode;
    onClick?: () => void;
}> = ({ children, onClick }) => (
    <TabStyled onClick={onClick}>{children}</TabStyled>
);

export const TabList: React.FC<{ children: React.ReactNode }> = ({
    children
}) => (
    <div style={{ borderTop: `1px solid ${colorCSS("N40")}` }}>
        <style>{`
            .tab-list > * {
                align-items: center;
                border-bottom: 1px solid ${colorCSS("N40")};
                display: flex;
                min-height: ${SMALL_NAV_PX_HEIGHT}px;
            }
        `}</style>
        <div className="tab-list">{children}</div>
    </div>
);
